// Typed client for the creator-side wiki endpoints (owner-authored project
// docs, hosted at their own public URL - see services/publish.ts for the
// sibling "publish the game itself" flow this deliberately mirrors but does
// not share a lifecycle with).
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, ApiHttpError } from './api';
import { captchaHeader, requestPublishCaptchaToken } from './captcha';

export type WikiVisibility = 'public' | 'unlisted' | 'private';

export interface Wiki {
  slug: string;
  title: string;
  visibility: WikiVisibility;
  url: string;
  updated_at: string | null;
}

export interface WikiPageSummary {
  id: string;
  slug: string;
  title: string;
  position: number;
  /** Free-text sidebar grouping label; null means ungrouped. Not a folder
   * hierarchy - see migration 0023 in sstk-studio-api for why. */
  section: string | null;
}

export interface WikiPage extends WikiPageSummary {
  body_markdown: string;
  updated_at: string | null;
}

export interface WikiPageVersion {
  version: number;
  title: string;
  created_at: string | null;
  live: boolean;
}

/** Creating a wiki publishes creator content at a public URL, so it is
 * captcha-gated the same way a first game publish is. */
export async function createWiki(
  projectId: string,
  body: { slug?: string; title?: string; visibility?: WikiVisibility },
): Promise<Wiki> {
  const token = await requestPublishCaptchaToken();
  return apiPost(`/projects/${projectId}/wiki`, body, captchaHeader(token));
}

/** 404s when the project has never created a wiki; callers treat that as null. */
export async function getWiki(projectId: string): Promise<Wiki | null> {
  try {
    return await apiGet<Wiki>(`/projects/${projectId}/wiki`);
  } catch (err) {
    if (err instanceof ApiHttpError && err.status === 404) return null;
    throw err;
  }
}

/** Only sending a visibility change to 'public' needs a fresh captcha solve
 * (mirrors the api's require_captcha_for_wiki_update: a title or slug edit
 * is low-risk and reversible). Callers do not need to know which - this only
 * asks for a token when the api will actually redeem one. */
export async function updateWiki(
  projectId: string,
  body: { slug?: string; title?: string; visibility?: WikiVisibility },
): Promise<Wiki> {
  const token = body.visibility === 'public' ? await requestPublishCaptchaToken() : '';
  return apiPatch(`/projects/${projectId}/wiki`, body, captchaHeader(token));
}

export function deleteWiki(projectId: string): Promise<{ status: string }> {
  return apiDelete(`/projects/${projectId}/wiki`);
}

export function listWikiPages(projectId: string): Promise<WikiPageSummary[]> {
  return apiGet(`/projects/${projectId}/wiki/pages`);
}

export function createWikiPage(
  projectId: string,
  body: { title: string; slug?: string; body_markdown?: string; section?: string },
): Promise<WikiPage> {
  return apiPost(`/projects/${projectId}/wiki/pages`, body);
}

export function getWikiPage(projectId: string, pageId: string): Promise<WikiPage> {
  return apiGet(`/projects/${projectId}/wiki/pages/${pageId}`);
}

/** Omitting `section` leaves it unchanged; passing "" clears it back to
 * ungrouped (mirrors the api's PageUpdate semantics). */
export function updateWikiPage(
  projectId: string,
  pageId: string,
  body: { title?: string; slug?: string; body_markdown?: string; section?: string },
): Promise<WikiPage> {
  return apiPut(`/projects/${projectId}/wiki/pages/${pageId}`, body);
}

export function listWikiPageVersions(projectId: string, pageId: string): Promise<WikiPageVersion[]> {
  return apiGet(`/projects/${projectId}/wiki/pages/${pageId}/versions`);
}

/** Restores a prior version as the page's live content immediately - a wiki
 * page has no separate publish step, unlike the game's own publication
 * versions (services/publish.ts's restorePublicationVersion). */
export function restoreWikiPageVersion(
  projectId: string,
  pageId: string,
  version: number,
): Promise<WikiPage> {
  return apiPost(`/projects/${projectId}/wiki/pages/${pageId}/versions/${version}/restore`);
}

export function deleteWikiPage(projectId: string, pageId: string): Promise<{ status: string }> {
  return apiDelete(`/projects/${projectId}/wiki/pages/${pageId}`);
}

/** page_ids must be exactly the wiki's current page ids, each once, in the
 * new order; the api rejects any other set with a 422. */
export function reorderWikiPages(projectId: string, pageIds: string[]): Promise<WikiPageSummary[]> {
  return apiPut(`/projects/${projectId}/wiki/pages/reorder`, { page_ids: pageIds });
}
